import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@rsos/base-components';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setTempMessage } from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import {
  TF911_CONVERSATION_ENDED,
  setConversationUnread,
  setPendingTF911Message,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { createTalkUser } from '@rsos/utils/talkJS';
import { useTalkJS, getTalkInstances } from '@rsos/utils/useTalkJS';
import { RootState } from '../../../../app';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { PRECONFIG_REQUEST_LOCATION } from '../../../PreconfiguredMessages/AllMessages/utils';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { ChatContainer } from './TF911.styles';
import {
  trackSendPreconfiguredResponse,
  trackViewAllPreconfiguredResponses,
} from './TF911ChatBoxTrackings';

interface TF911ChatInterface {
  callerID: string;
  showPreconfiguredMessages: boolean;
  showStartChat: boolean;
  isTextFrom911ChatBoxVisible: boolean;
  setIsAllMessagesVisible: (isVisible: boolean) => void;
  selectedPreconfiguredMessage: PreconfiguredMessageInterface | null;
  setSelectedPreconfiguredMessage: (
    message: PreconfiguredMessageInterface | null,
  ) => void;
  preconfiguredMessages: PreconfiguredMessageInterface[];
  hasRequestLocationAccess: boolean;
}

/**
 * Create a TalkJS chat session for a given caller data.
 * Also acts as a proof of concept for minimizing mount utilization for TalkJS.
 * Expect bugs, not in a finished state
 */
const TF911Chat = ({
  callerID,
  isTextFrom911ChatBoxVisible,
  preconfiguredMessages,
  setIsAllMessagesVisible,
  selectedPreconfiguredMessage,
  setSelectedPreconfiguredMessage,
  hasRequestLocationAccess,
}: TF911ChatInterface) => {
  const dispatch = useDispatch();
  const tempMessage = useSelector(
    (state: RootState) => state.outboundSms.tempMessage,
  );
  const selectedTab = useSelector(
    (state: RootState) => state.eventPane.selectedTab,
  );
  const isTextFrom911ChatVisibleRedux = useSelector(
    (state: RootState) =>
      state.eventPane.chatVisibility?.isTextFrom911ChatVisible,
  );

  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const chatTheme = useTalkJSTheme(true);

  const conversationID = useSelectedTextFrom911ConversationID(callerID);

  const conversation = useSelectedTextFrom911Conversation(callerID);
  const hasUnreadMessages = conversation?.ui?.hasUnreadMessages;

  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );
  const pendingTF911Message = useSelector(
    (state: RootState) => state.textFrom911.pendingTF911Message,
  );
  const selectedConversation = useSelector(
    (state: RootState) => state.textFrom911.selectedConversation,
  );

  const shouldShowChatButtons =
    !isConversationEnded &&
    isTextFrom911ChatBoxVisible &&
    conversationID &&
    !isOutOfJurisdiction;

  const hasSuggestedResponses =
    hasAccessToUniteEventPane && shouldShowChatButtons;

  const loggedInUser = useTF911LoggedInUser();

  const messageFieldVisibility = useMemo(() => {
    if (isOutOfJurisdiction) {
      return false;
    }

    return !isConversationEnded;
  }, [isConversationEnded, isOutOfJurisdiction]);

  const shellRef = useTalkJS({
    theme: chatTheme,
    user: loggedInUser,
  });

  // Set Conversation
  useEffect(() => {
    const { chat, session } = getTalkInstances(chatTheme);
    if (session && conversationID && chat) {
      const conversation = session.getOrCreateConversation(conversationID);
      if (pendingTF911Message && selectedConversation) {
        conversation.sendMessage(pendingTF911Message);
        dispatch(setPendingTF911Message(null));
      }
      // TF911 Conversation Setup

      conversation.setParticipant(createTalkUser(loggedInUser));
      chat.select(conversation);

      chat.messageField.setVisible(messageFieldVisibility);

      session.onMessage(message => {
        // Handle preconfigured messages tracking
        const matchedMessage = preconfiguredMessages?.find(
          msg => msg.body === message.body,
        );

        if (matchedMessage) {
          trackSendPreconfiguredResponse(matchedMessage, conversationID);
        }
      });
    }
  }, [
    conversationID,
    isConversationEnded,
    loggedInUser,
    preconfiguredMessages,
    pendingTF911Message,
    selectedConversation,
  ]);

  useEffect(() => {
    const { chat, session } = getTalkInstances(chatTheme);
    // use redux state for visibility, but fall back to prop for backward compatibility
    const chatVisible =
      isTextFrom911ChatVisibleRedux !== undefined
        ? isTextFrom911ChatVisibleRedux
        : isTextFrom911ChatBoxVisible;
    if (
      selectedTab === CHAT_TAB &&
      chatVisible &&
      conversationID &&
      session &&
      chat &&
      isTextFrom911ChatBoxVisible &&
      hasUnreadMessages
    ) {
      dispatch(setConversationUnread({ callerID, hasUnreadMessages: false }));
    }
  }, [
    selectedTab,
    isTextFrom911ChatVisibleRedux,
    conversationID,
    isTextFrom911ChatBoxVisible,
    hasUnreadMessages,
    callerID,
    dispatch,
  ]);

  // Set messageField text
  useEffect(() => {
    const { chat } = getTalkInstances(chatTheme);
    if (chat && !isConversationEnded) {
      if (selectedPreconfiguredMessage) {
        chat.messageField.setText(selectedPreconfiguredMessage.body);
        setSelectedPreconfiguredMessage(null);
      }
      if (tempMessage) {
        chat.messageField.setText(tempMessage);
        dispatch(setTempMessage(null));
      }
    }
  }, [
    selectedPreconfiguredMessage,
    isConversationEnded,
    tempMessage,
    setSelectedPreconfiguredMessage,
    dispatch,
  ]);

  const handleViewClick = () => {
    trackViewAllPreconfiguredResponses(conversationID);
    setIsAllMessagesVisible(true);
  };

  const handleRequestLocation = (url: string) => {
    const { chat } = getTalkInstances(chatTheme);
    if (chat && !isConversationEnded && preconfiguredMessages) {
      const locationMessage = preconfiguredMessages.find(
        message => message.title === PRECONFIG_REQUEST_LOCATION,
      );

      const talkjsMessage = locationMessage?.body.replace(/\{\{.*?\}\}/g, url);
      if (talkjsMessage) {
        chat.messageField.setText(talkjsMessage);
      }
    }
  };

  return (
    <ChatContainer
      height="100%"
      display={isTextFrom911ChatBoxVisible && conversationID ? 'flex' : 'none'}
      showBorder={isTextFrom911ChatBoxVisible && conversationID}
      border="1px solid"
      borderRadius="0 5px 5px 0"
      borderColor="lightLineDivider"
    >
      <Box ref={shellRef} height="100%" />
      {hasSuggestedResponses && (
        <Box width="100%" horizontal align="center">
          {hasRequestLocationAccess && (
            <RequestLocation onSuccess={handleRequestLocation} />
          )}
          <SuggestedResponses handleViewClick={handleViewClick} />
        </Box>
      )}
    </ChatContainer>
  );
};
export default TF911Chat;
